import {DOCUMENT} from '@angular/platform-browser';
import {StsHttpService} from 'app/services/sts-http.service';
import {StsUser} from 'app/models/sts-user.model';
import {SigninResult} from 'app/models/signin-result.model';
import {Router} from '@angular/router';
import {FormBuilder, FormGroup, FormControl} from '@angular/forms';
import {StsNotification} from '../../../models/sts-notification.model';
import {StsNotificationType} from '../../../models/sts-notification-type';
import {StsTenantService} from 'app/services/sts-tenant.service';
import {StsTenant} from '../../../models/sts-tenant.model';
import {StsDialogFactoryService} from 'app/services/sts-dialog-factory.service';
import {StsButtonComponent} from 'app/components/common/sts-button/sts-button.component';
import {StsSessionTimeoutService} from 'app/services/sts-session-timeout.service';
import {StsCmsService} from '../../../services/sts-cms-service.service';
import {StsUserService} from '../../../services/sts-user.service';
import {
  Component,
  OnInit,
  AfterViewInit,
  Renderer,
  Inject,
  ViewContainerRef,
  ComponentFactoryResolver,
  ViewChild
} from '@angular/core';
import {forkJoin} from 'rxjs';
import {add} from 'lodash';
import {StsRootConfigObject} from '../../../models/sts-account-config.model';

@Component({
  selector: 'app-account-signin',
  templateUrl: './account-signin.component.html',
  styleUrls: ['./account-signin.component.scss']
})
export class AccountSigninComponent implements OnInit, AfterViewInit {

  signInUrl = '/api/account/login';
  stsUser: StsUser = new StsUser();
  signUpForm: FormGroup;
  tenantInfo: StsTenant = new StsTenant();
  public disableSubmit = false;

  usernameValid = true;
  passwordValid = true;

  public errors: StsNotification[] = [];
  public communicationChannels: any;

  @ViewChild(StsButtonComponent) submitButton;

  constructor(private stsHttp: StsHttpService,
              private router: Router,
              private fb: FormBuilder,
              private tenantService: StsTenantService,
              private timeoutService: StsSessionTimeoutService,
              private renderer: Renderer,
              @Inject(DOCUMENT) private document,
              private componentFactoryResolver: ComponentFactoryResolver,
              private viewContainerRef: ViewContainerRef,
              private dialogFactory: StsDialogFactoryService,
              private userService: StsUserService) {
  }

  ngOnInit() {
    this.timeoutService.keepAlive();
  }

  ngAfterViewInit() {
    this.tenantService.getTenantSource().subscribe(tenant => {
      this.tenantInfo = tenant;
    });
  }

  sidebarLoaded(complete) {
    if (complete) {
      this.dialogFactory.createDialog(this.componentFactoryResolver, this.viewContainerRef, this.document, this.renderer);
    }
  }

  private validateFields() {
    this.usernameValid = !!this.stsUser.userName;
    this.passwordValid = !!this.stsUser.password;

    return this.usernameValid && this.passwordValid;
  }

  onSubmit() {

    this.disableSubmit = false;

    if (!this.validateFields()) {
      return;
    }

    this.disableSubmit = true;

    this.submitButton.onSubmit();
    // let params = new HttpParams()
    //   .set('username', encodeURIComponent(this.stsUser.userName))
    //   .set('password', encodeURIComponent(this.stsUser.password));
    let params = {
      username: btoa(this.stsUser.userName || ''),
      password: btoa(this.stsUser.password || ''),
      encoded: true,
    };

    this.stsHttp.postRegular<SigninResult>(this.signInUrl, params)
      .subscribe((result) => {
        this.disableSubmit = false;
        this.errors = [];
        if (result.success) {
          this.tenantService.getConfigSource().subscribe(cfg => {
            this.userService.getProfile().subscribe(
              user => {
                let hasEmailOnFile = user && (user.hasEmailOnFile || user.email || user.personalEmail);
                this.loginWorkflow(result, user && user.externalUserId, cfg.enableEmailCollectionOnLogin, hasEmailOnFile);
              },
              err => {
                //if we get any typeof error from this service then we will roll back to previous workflow
                this.loginWorkflow(result, null, false, false);
              });
          });
        } else {
          let err = new StsNotification();
          err.code = result.code || 'sts-login-message';
          err.description = result.message;
          err.level = StsNotificationType.Error;
          this.errors.push(err);
          this.submitButton.onSubmitFinished();
        }
      });
  }

  private loginWorkflow(result: any, contactId: any, collectEmailOnLogin: boolean, hasEmailOnFile: boolean): void {
    if (result.status && result.status === 'ACTIONREQUIRED_CHPWD') {
      this.router.navigateByUrl('/account/password/change'); // login status forces password change, that means it has expired,
      // so we set route param to 'change' to distinguish from the case where user decides to change password voluntarily
    } else if (contactId != null && !this.tenantInfo.isUseEmailOnFileForRegistrationEnabled && this.tenantInfo.isEmailCollectionEnabled && !hasEmailOnFile) {
      if (!collectEmailOnLogin) {
        let err = new StsNotification();
        err.code = 'sts-email-not-available';
        err.description = 'You don\'t have an email on file. Please contact your administrator.';
        err.level = StsNotificationType.Error;
        this.errors.push(err);
        this.submitButton.onSubmitFinished();
        return;
      }
      //Collect email address if required
      //Need to get communication Channels
      var url = '/account/collect/email/' + contactId + '/true/' + (result.status && result.status === 'ACTIONREQUIRED_2FA_VERIFICATION' ? 'true' : 'false');
      this.router.navigateByUrl(url);
    } else if (result.status && result.status === 'ACTIONREQUIRED_2FA_VERIFICATION') {
      if (!hasEmailOnFile) {
        let err = new StsNotification();
        err.code = 'sts-email-not-available';
        err.description = 'You don\'t have an email on file. Please contact your administrator.';
        err.level = StsNotificationType.Error;
        this.errors.push(err);
        this.submitButton.onSubmitFinished();
        return;
      }
      this.redirectTo2FAVerification();
    } else {
      document.location.href = '/';
    }
  }

  private redirectTo2FAVerification(): void {
    this.userService.getCommunicationChannels().subscribe(result => {
      if (result) {
        let deliveryChannels = this.tenantInfo.tfaDeliveryChannels;
        let communicationChannels = result.communicationChannels.map(channel => channel.Type);
        // channels available to the user
        let availableChannels = communicationChannels.filter(channel => deliveryChannels.includes(channel));

        // many delivery channels available
        if (availableChannels.length > 1 && availableChannels.includes('SMS')) {
          this.router.navigateByUrl('/account/2fa/sel-comm');
        } else if (availableChannels.includes('SMS')) { // only sms delivery available
          this.router.navigateByUrl('/account/2fa/validate/phone');
        } else if (availableChannels.includes('EMAIL')) { // only email delivery available
          this.router.navigateByUrl('/account/2fa/validate/email');
        } else { // no channels available
          this.notify('sts-no-communication-channels', 'You have no communication channels on file', StsNotificationType.Error);
          this.submitButton.onSubmitFinished();
        }
      }
    });
  }

  private notify(code: string, description: string, type: StsNotificationType): void {
    let notification = new StsNotification();
    notification.code = code;
    notification.description = description;
    notification.level = type;

    this.errors.push(notification);
  }
}

